import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import PageHeader from "components/Common/PageHeader";
import SideBarSchoolSettings from "feature-module/settings/components/SideBarSchoolSettings";
import { all_routes } from "router/all_routes";

type SettingItem = {
  key: string;
  icon: string;        // Tabler icon class e.g. "ti ti-settings"
  title: string;       // i18n key
  description: string; // i18n key
  to: string;
};

const Section: React.FC<{ titleKey: string; items: SettingItem[] }> = ({ titleKey, items }) => {
  const { t } = useTranslation();
  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="fw-semibold mb-0 text-dark">
          {t(titleKey)}
        </h4>
        <span className="flex-grow-1 border-bottom border-2 border-light ms-3"></span>
      </div>
      <div className="row g-3">
        {items.map((it) => (
          <div key={it.key} className="col-12 col-md-6 col-xl-4">
            <Link to={it.to} className="text-decoration-none text-reset">
              <div className="card h-100">
                <div className="card-body d-flex">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0 me-3"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      backgroundColor: "rgba(106, 13, 173, 0.08)", // light violet shade
                    }}
                  >
                    <i className={`${it.icon} text-primary fs-4`} />
                  </div>
                  <div>
                    <h5 className="mb-1">{t(it.title)}</h5>
                    <div className="text-muted">{t(it.description)}</div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();



  // ===== Sections built from your routes =====
  const general: SettingItem[] = [
    {
      key: "instance",
      icon: "ti ti-settings",
      title: "settingsPage.home.instance.title",
      description: "settingsPage.home.instance.desc",
      to: all_routes.instanceSettings,
    },
    {
      key: "planification",
      icon: "ti ti-calendar-time",
      title: "settingsPage.home.planification.title",
      description: "settingsPage.home.planification.desc",
      to: all_routes.planificationSettings,
    },
    {
      key: "absence",
      icon: "ti ti-user-exclamation",
      title: "settingsPage.home.absence.title",
      description: "settingsPage.home.absence.desc",
      to: all_routes.absenceSettings,
    },
    {
      key: "messaging",
      icon: "ti ti-message",
      title: "settingsPage.home.messaging.title",
      description: "settingsPage.home.messaging.desc",
      to: all_routes.messagingSettings,
    }
  ];

  const users: SettingItem[] = [
    // {
    //   key: "profile",
    //   icon: "ti ti-id",
    //   title: "settingsPage.home.profile.title",
    //   description: "settingsPage.home.profile.desc",
    //   to: all_routes.profile,
    // },
    // {
    //   key: "userManagement",
    //   icon: "ti ti-users",
    //   title: "settingsPage.home.userManagement.title",
    //   description: "settingsPage.home.userManagement.desc",
    //   to: all_routes.userManagement,
    // },
    {
      key: "rolesPermissions",
      icon: "ti ti-shield-lock",
      title: "settingsPage.home.roles.title",
      description: "settingsPage.home.roles.desc",
      to: all_routes.rolesPermissions,
    },
    // {
    //   key: "auditLogs",
    //   icon: "ti ti-file-analytics",
    //   title: "settingsPage.home.auditLogs.title",
    //   description: "settingsPage.home.auditLogs.desc",
    //   to: all_routes.auditLogs,
    // },
  ];

  const academic: SettingItem[] = [
    {
      key: "subjectCoefficients",
      icon: "ti ti-notes",
      title: "settingsPage.home.subjectCoefficients.title",
      description: "settingsPage.home.subjectCoefficients.desc",
      to: all_routes.subjectCoefficients,
    },
  ];

  const finance: SettingItem[] = [
    {
      key: "feesTypes",
      icon: "ti ti-receipt-2",
      title: "settingsPage.home.feesTypes.title",
      description: "settingsPage.home.feesTypes.desc",
      to: all_routes.feesTypes,
    },
    {
      key: "expenseCategories",
      icon: "ti ti-category",
      title: "settingsPage.home.expenseCategories.title",
      description: "settingsPage.home.expenseCategories.desc",
      to: all_routes.expenseCategories,
    },
    {
      key: "suppliers",
      icon: "ti ti-truck-delivery",
      title: "settingsPage.home.suppliers.title",
      description: "settingsPage.home.suppliers.desc",
      to: all_routes.suppliers,
    },
  ];

  const system: SettingItem[] = []; // keep for future (backups, branding, etc.)

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader title={t("settingsPage.instance_settings")} breadcrumbItems={[]} />

        <div className="row">
          <Section titleKey="settingsPage.home.general" items={general} />
          <Section titleKey="settingsPage.home.userManagementSection" items={users} />
          <Section titleKey="settingsPage.home.academicSection" items={academic} />
          <Section titleKey="settingsPage.home.financeSection" items={finance} />
          {system.length > 0 && <Section titleKey="settingsPage.home.systemSection" items={system} />}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
